import axios from '@/libs/api.request';
import JSEncrypt from 'jsencrypt';
import CryptoJS from 'crypto-js';
import store from '@/store';

export const getQuestionConfig = questionnaireCode =>
    axios.request({
        url: `/questionnaire/${questionnaireCode}/begin`
    });

/**
 * RSA加密工具类 - 与Java后端RSA/ECB/PKCS1Padding兼容
 */
class RSAEncryptor {
    constructor() {
        this.jsEncrypt = new JSEncrypt();
        // JSEncrypt默认使用PKCS1填充，与Java的RSA算法默认填充一致
    }

    /**
     * 将Base64公钥转换为PEM格式
     * @param {string} base64Key - Base64格式的公钥
     * @returns {string} PEM格式的公钥
     */
    formatPublicKey(base64Key) {
        if (!base64Key) return '';

        // 如果已经是PEM格式，直接返回
        if (base64Key.includes('-----BEGIN')) {
            return base64Key;
        }

        // 移除可能的空白字符（与后端cleanKey方法保持一致）
        const cleanKey = base64Key.replace(/\s+/g, '');

        // 添加PEM头尾（JSEncrypt需要完整的PEM格式）
        return `-----BEGIN PUBLIC KEY-----\n${cleanKey}\n-----END PUBLIC KEY-----`;
    }

    /**
     * 设置公钥
     * @param {string} publicKey - 公钥（Base64格式，与Java SeededRSAUtil兼容）
     */
    setPublicKey(publicKey) {
        const pemPublicKey = this.formatPublicKey(publicKey);
        this.jsEncrypt.setPublicKey(pemPublicKey);
    }

    /**
     * 加密数据 - 与Java Cipher.getInstance("RSA")兼容
     * Java默认使用: RSA/ECB/PKCS1Padding
     * JSEncrypt默认也使用PKCS1填充
     * @param {string} data - 要加密的数据
     * @returns {string} 加密后的数据（Base64格式，与Java Base64.getDecoder().decode()兼容）
     */
    encrypt(data) {
        if (typeof data === 'object') {
            data = JSON.stringify(data);
        }

        // 确保数据为UTF-8字符串（与Java new String(decryptedBytes)兼容）
        const utf8Data = data.toString();

        const encrypted = this.jsEncrypt.encrypt(utf8Data);

        if (!encrypted) {
            throw new Error('加密失败，可能是公钥格式不正确');
        }

        return encrypted;
    }
}

// 创建全局加密实例
const rsaEncryptor = new RSAEncryptor();

// 导出加密器供测试页面使用
export { rsaEncryptor };

/**
 * 生成随机AES密钥
 * @returns {string} 32字节的随机密钥（Base64格式）
 */
const generateAESKey = () => {
    return CryptoJS.lib.WordArray.random(256 / 8).toString(CryptoJS.enc.Base64);
};

/**
 * 使用AES加密内容
 * @param {string} content - 要加密的内容
 * @param {string} key - AES密钥（Base64格式）
 * @returns {string} 加密后的内容（Base64格式）
 */
const encryptWithAES = (content, key) => {
    const keyWordArray = CryptoJS.enc.Base64.parse(key);
    const encrypted = CryptoJS.AES.encrypt(content, keyWordArray, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
    });
    return encrypted.toString();
};

/**
 * 使用RSA公钥加密AES密钥
 * @param {string} aesKey - AES密钥（Base64格式）
 * @param {string} publicKey - RSA公钥
 * @returns {string} 加密后的AES密钥（Base64格式）
 */
const encryptAESKeyWithRSA = (aesKey, publicKey) => {
    try {
        rsaEncryptor.setPublicKey(publicKey);
        return rsaEncryptor.encrypt(aesKey);
    } catch (error) {
        throw new Error('RSA加密AES密钥失败: ' + error.message);
    }
};

/**
 * 使用AES加密内容（使用共享密钥）
 * @param {string} content - 要加密的内容
 * @param {string} aesKey - 共享的AES密钥
 * @returns {string} 加密后的内容
 */
const encryptContentWithSharedKey = (content, aesKey) => {
    if (!content || !aesKey) {
        return content;
    }

    try {
        return encryptWithAES(content, aesKey);
    } catch (error) {
        return content;
    }
};
/**
 * 递归遍历answer对象，使用共享AES密钥加密所有answerContent字段
 * @param {object} obj - 要处理的对象
 * @param {string} aesKey - 共享的AES密钥
 * @returns {object} 处理后的对象
 */
const encryptAnswerData = (obj, aesKey) => {
    if (!obj || !aesKey) {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => encryptAnswerData(item, aesKey));
    }

    if (typeof obj === 'object') {
        const result = {};
        for (const key in obj) {
            if (key === 'answerContent' && obj[key]) {
                // 对answerContent进行AES加密
                result[key] = encryptContentWithSharedKey(obj[key], aesKey);
            } else {
                // 递归处理其他字段
                result[key] = encryptAnswerData(obj[key], aesKey);
            }
        }
        return result;
    }

    return obj;
};

/**
 * 提交问卷
 * @param {*} questionnaireCode
 * @param {*} answer
 * @returns
 */
export const postQuestionAnswer = (questionnaireCode, answer) => {
    // 获取公钥
    const publicKey = store.state.publicKey;

    if (!publicKey) {
        // 如果没有公钥，直接提交原始数据
        return axios.request({
            url: `/questionnaire/${questionnaireCode}/submit-questionnaire`,
            data: answer,
            method: 'post'
        });
    }

    try {
        // 生成一个共享的AES密钥
        const aesKey = generateAESKey();

        // 使用共享密钥加密所有answerContent
        const encryptedAnswer = encryptAnswerData(answer, aesKey);

        // 使用RSA加密AES密钥
        const encryptedKey = encryptAESKeyWithRSA(aesKey, publicKey);

        // 构建请求数据
        const requestData = {
            ...encryptedAnswer,
            key: encryptedKey // 添加RSA加密的AES密钥
        };

        return axios.request({
            url: `/questionnaire/${questionnaireCode}/submit-questionnaire`,
            data: requestData,
            method: 'post'
        });
    } catch (error) {
        // 加密失败时，提交原始数据
        return axios.request({
            url: `/questionnaire/${questionnaireCode}/submit-questionnaire`,
            data: answer,
            method: 'post'
        });
    }
};

/**
 * 抽奖
 * @param {*} code
 * @param {*} gToken
 * @returns
 */
export const postReward = (code, gToken) =>
    axios.request({
        url: `/questionnaire/reward/${code}`,
        method: 'post',
        params: {
            gToken
        }
    });
/**
 * 问卷上传图片
 * @param {*} qidentifier
 * @param {*} questionnaireCode
 * @returns
 */
export const uploadImage = (file, { qidentifier, questionnaireCode }) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.request({
        url: `/questionnaire/${questionnaireCode}/upload-resource`,
        method: 'post',
        data: formData,
        params: {
            qIdentifier: qidentifier
        }
    });
    // const url = new URL(
    //     `http://172.31.236.43:8070/xx/questionnaire/api/questionnaire/${questionnaireCode}/upload-resource`
    // );
    // url.searchParams.append('qIdentifier', qidentifier);

    // return fetch(url.toString(), {
    //     method: 'POST',
    //     body: formData
    //     // 如果需要携带cookie等凭证
    //     // credentials: 'include'
    // })
    //     .then(response => {
    //         if (!response.ok) {
    //             throw new Error(`HTTP error! status: ${response.status}`);
    //         }
    //         return response.json();
    //     })
    //     .catch(error => {
    //         console.error('上传失败:', error);
    //         throw error;
    //     });
};

export const uploadImage1 = (base64Str, { qidentifier, questionnaireCode }) => {
    const formData = new FormData();
    // formData.append('file', file);
    formData.append('fileBase64', base64Str);
    formData.append('fileName', Date.now() + '.jpg');

    return axios.request({
        url: `/questionnaire/${questionnaireCode}/upload-resource-base64`,
        method: 'post',
        data: formData,
        params: {
            qIdentifier: qidentifier
        }
    });
};

/**
 * 获取用户已经上传的图片资源
 * @param {*} questionnaireCode
 * @returns
 */
export const getExistImages = questionnaireCode => {
    return axios.request({
        url: `/questionnaire/${questionnaireCode}/resource-exist`,
        method: 'get'
    });
};
/**
 * 删除图片资源
 * @param {*} param0
 * @returns
 */
export const deleteImage = ({ questionnaireCode, qIdentifier, fileCode }) => {
    return axios.request({
        url: `/questionnaire/${questionnaireCode}/delete-resource`,
        method: 'post',
        params: {
            qIdentifier,
            fileCode
        }
    });
};

// 导出加密相关函数
export { encryptContentWithSharedKey, encryptAnswerData };
